import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Die Sprachen des Ladenfensters — ohne Fremdbibliothek.
 *
 * Aus demselben Grund, aus dem oben in styles.css keine UI-Bibliothek steht:
 * Ein Laden mit acht Produkten braucht keine 300 kB Fremdcode, um zweihundert
 * Sätze in zwei Sprachen zu halten. Was eine i18n-Bibliothek darüber hinaus
 * kann — Namensräume, Nachladen je Seite, ICU-Botschaften mit Geschlecht und
 * Ordnungszahlen — bräuchte dieser Laden an keiner Stelle, und die
 * Zahlenformate kann Intl ohnehin selbst.
 *
 * Deutsch ist die Vorgabe und zugleich die Quelle: `de` legt fest, welche
 * Schlüssel es gibt, jede weitere Sprache ist ein `Record<Textschluessel,
 * string>`. Fehlt dort eine Zeichenkette, ist das ein Typfehler und kein
 * stiller Rückfall aufs Deutsche — genau darum geht es. Eine halb übersetzte
 * Oberfläche ist schlechter als eine deutsche: Sie sieht kaputt aus, und
 * niemand merkt, welche Zeile fehlt, weil ja etwas dasteht.
 *
 * Eine dritte Sprache ist damit eine Datei und ein Eintrag in SPRACHEN.
 *
 * ---------------------------------------------------------------------------
 * Was hier bewusst NICHT steht, und warum
 * ---------------------------------------------------------------------------
 *
 * Übersetzt wird nur, was das Ladenfenster selbst sagt — also das, was bei
 * jedem Laden gleich dasteht: Knöpfe, Überschriften, Hinweise, Fehlermeldungen
 * des Browsers. Drei Sorten Text bleiben in der Sprache, in der sie ankommen:
 *
 *   Produktnamen und Beschreibungen kommen aus der Kasse. Dort pflegt der
 *   Ladeninhaber einen Namen, keine zwei. Ein Wörterbuch im Frontend könnte
 *   ihn nur raten, und ein geratener Produktname ist schlimmer als ein
 *   deutscher.
 *
 *   Die Seiten des Ladens — Impressum, Widerruf, Datenschutz — sind Freitext
 *   aus dem Backoffice. Sie sind obendrein Rechtstexte: Eine maschinell
 *   übersetzte Widerrufsbelehrung wäre keine Belehrung mehr, sondern ein
 *   Haftungsfall.
 *
 *   Die Meldungen des Servers (`blockers`, die Hinweise der Zahlungsarten, der
 *   Text einer ApiError) sind fertige deutsche Sätze. Der Server kennt den
 *   Mindestbestellwert und die Lieferländer dieses Ladens, das Ladenfenster
 *   nicht — es könnte sie nur neu erfinden, und dann stünde dort etwas
 *   anderes, als der Server meint.
 *
 * Kurz: Übersetzt wird, was im Ladenfenster steht. Was der Laden hineinlegt,
 * bleibt seins.
 */

// ---------- Deutsch: die Vorgabe und die Liste aller Schlüssel ----------

const de = {
  // --- Gerüst, Kopf, Fuß ---
  'app.oeffnet': 'Laden wird geöffnet …',
  'app.fehlerTitel': 'Der Laden ist gerade nicht erreichbar',
  'app.fehlerText':
    'Wir konnten die Angaben zu diesem Laden nicht laden. Bitte versuchen Sie es in einigen Minuten noch einmal.',
  'layout.zumInhalt': 'Zum Inhalt springen',
  'nav.aria': 'Hauptnavigation',
  'nav.startseite': 'Startseite',
  'nav.sammlungen': 'Sammlungen',
  'nav.bestellungVerfolgen': 'Bestellung verfolgen',
  'nav.meineBestellungen': 'Meine Bestellungen',
  'nav.suchen': 'Suchen',
  'suche.feldLabel': 'Im Laden suchen',
  'suche.platzhalter': 'Suchen …',
  'suche.knopf': 'Suchen',
  'suche.fensterHinweis':
    'Nach Produktnamen suchen — oder leer lassen und das ganze Sortiment ansehen.',
  'fuss.rechtlichesAria': 'Rechtliches und Informationen',
  'fuss.mwst': 'Alle Preise verstehen sich inklusive Mehrwertsteuer.',
  'sprache.label': 'Sprache',
  'sammlung.sortieren': 'Sortieren',
  'sammlung.sortEmpfohlen': 'Empfohlen',
  'sammlung.sortPreisAuf': 'Preis: aufsteigend',
  'sammlung.sortPreisAb': 'Preis: absteigend',
  'sammlung.sortName': 'Name: A–Z',
  // --- Schaufenster (neu) ---
  'home.sortimentAnsehen': 'Sortiment ansehen',
  'home.sammlungenEntdecken': 'Sammlungen entdecken',
  'home.sammlungenTitel': 'Nach Sammlung einkaufen',
  'konto.menue': 'Konto',
  'konto.menueAngemeldet': 'Konto von {name}',
  'nav.menue': 'Menü',
  'nav.menueSchliessen': 'Menü schließen',
  'fuss.einkaufen': 'Einkaufen',
  'fuss.service': 'Service',
  'fuss.rechtliches': 'Rechtliches',
  'fuss.rechte': '© {jahr} {laden}',
  'kachel.ausverkauftMarke': 'Ausverkauft',
  'kachel.inDenKorb': '{name} in den Warenkorb legen',
  'kachel.gelegt': '{name} liegt im Warenkorb.',
  'kachel.nichtGelegt': '{name} ist gerade nicht mehr vorrätig.',
  'produkt.menge': 'Menge',
  'produkt.mengeWeniger': 'Eins weniger',
  'produkt.mengeMehr': 'Eins mehr',
  'produkt.versandTitel': 'Versand',
  'produkt.versandFrei': 'Kostenloser Versand',
  'produkt.versandMitGrenze': '{preis}, ab {grenze} kostenlos',
  'produkt.versandPauschal': '{preis} je Bestellung',
  'produkt.abholungTitel': 'Abholung im Laden',
  'produkt.abholungText': 'Kostenlos. Wir sagen Bescheid, sobald es bereitliegt.',
  'produkt.versandRueckgabe': 'Versand und Rückgabe',
  'produkt.versandRueckgabeText': 'Was der Versand kostet, wie lange er dauert und wie Sie etwas zurückgeben, steht in unseren Bedingungen.',
  'meldung.aria': 'Meldungen',
  'meldung.schliessen': 'Meldung schließen',

  // --- Konto ---
  'konto.angemeldet': 'Angemeldet',
  'konto.abmelden': 'Abmelden',
  'konto.meldetAb': 'Wird abgemeldet …',
  'konto.anmelden': 'Anmelden',
  'anmeldung.abgebrochen': 'Die Anmeldung wurde abgebrochen. Sie können auch ohne Konto bestellen.',
  'anmeldung.abgelaufen': 'Die Anmeldung hat zu lange gedauert. Bitte versuchen Sie es noch einmal.',
  'anmeldung.fehlgeschlagen':
    'Die Anmeldung hat nicht geklappt. Bitte versuchen Sie es noch einmal oder bestellen Sie ohne Konto.',

  // --- Zustände ---
  'zustand.laedt': 'Wird geladen …',
  'zustand.nichtErreichbar':
    'Der Laden ist gerade nicht erreichbar. Bitte versuchen Sie es in einem Moment noch einmal.',
  'zustand.schiefgegangen': 'Beim Laden ist etwas schiefgegangen. Bitte laden Sie die Seite neu.',
  'zustand.meldungServer': 'Meldung des Servers: ',

  // --- Formulare ---
  'feld.freiwillig': '(freiwillig)',

  // --- Brotkrumen, Blättern ---
  'krumen.aria': 'Sie befinden sich hier',
  'blaettern.aria': 'Seiten der Trefferliste',
  'blaettern.zurueck': 'Zurück',
  'blaettern.weiter': 'Weiter',
  'blaettern.seiteVon': 'Seite {seite} von {seiten}',

  // --- Kacheln und Bilder ---
  'kachel.ab': 'ab {preis}',
  'kachel.ausverkauft': 'derzeit ausverkauft',
  'kachel.ohneBild': 'ohne Bild',
  'kachel.keinBildZu': 'Kein Bild zu {name}',
  'galerie.keinBildZu': 'Kein Bild zu {name} vorhanden',
  'galerie.bildAnzeigen': 'Bild {nummer} von {anzahl} anzeigen',

  // --- Startseite ---
  'home.seoTitel': '{laden} — Onlineshop',
  'home.seoText': 'Der Onlineshop von {laden}. Sammlungen und Produkte ansehen.',
  'home.alleSammlungen': 'Alle Sammlungen',
  'home.sortiment': 'Aus dem Sortiment',
  'home.allesAnsehen': 'Alles ansehen',
  'home.zusagenAria': 'Was dieser Laden zusagt',

  // --- Sammlungen ---
  'sammlung.eine': 'Sammlung',
  'sammlung.seoTitelAlle': 'Sammlungen — {laden}',
  'sammlung.seoTextAlle': 'Alle Sammlungen im Onlineshop von {laden}.',
  'sammlung.seoTitel': '{name} — {laden}',
  'sammlung.seoText': 'Produkte der Sammlung {name} bei {laden}.',
  'sammlung.keine': 'Dieser Laden hat noch keine Sammlungen angelegt.',
  'sammlung.laedt': 'Sammlungen werden geladen …',
  'sammlung.leer': 'In dieser Sammlung liegt gerade nichts.',
  'sammlung.nichtGefunden': 'Diese Sammlung gibt es nicht (mehr).',
  'sammlung.produktEins': '1 Produkt',
  'sammlung.produktViele': '{anzahl} Produkte',

  // --- Suche ---
  'suche.seoTitel': 'Suche nach „{begriff}" — {laden}',
  'suche.seoTitelAlle': 'Alle Produkte — {laden}',
  'suche.seoText': 'Suchergebnisse für „{begriff}" im Onlineshop von {laden}.',
  'suche.seoTextAlle': 'Alle Produkte im Onlineshop von {laden}.',
  'suche.krume': 'Suche',
  'suche.alleProdukte': 'Alle Produkte',
  'suche.ueberschrift': 'Suche nach „{begriff}"',
  'suche.trefferKeine': 'Keine Treffer',
  'suche.trefferEiner': '1 Treffer',
  'suche.trefferViele': '{anzahl} Treffer',
  'suche.nichtsGefunden': 'Zu „{begriff}" haben wir nichts gefunden. Vielleicht hilft ein kürzeres Wort.',
  'produkt.keine': 'Es sind noch keine Produkte veröffentlicht.',

  // --- Produktseite ---
  'produkt.seoTitel': '{name} — {laden}',
  'produkt.seoTitelLeer': 'Produkt — {laden}',
  'produkt.nichtGefunden': 'Dieses Produkt gibt es nicht (mehr).',
  'produkt.preisAufAnfrage': 'Preis auf Anfrage',
  'produkt.abPreis': 'ab {preis}',
  'produkt.steuerhinweis': 'inkl. MwSt., zzgl. Versand',
  'produkt.beschreibung': 'Beschreibung',
  'produkt.keineZusammenstellung': 'Diese Zusammenstellung gibt es nicht. Bitte wählen Sie eine andere.',
  'produkt.ausverkauft': 'Ausverkauft',
  'produkt.andereAuswahl': ' — andere Auswahl ist möglicherweise vorrätig.',
  'produkt.vorraetig': 'Vorrätig',
  'produkt.nurNoch': ' — nur noch {anzahl} Stück',
  'variante.ausverkauft': 'ausverkauft',
  'variante.nichtKombinierbar': 'nicht kombinierbar',
  'variante.nichtKombinierbarTitel':
    '{achse} {wert} gibt es nicht mit der aktuellen Auswahl — ein Klick passt die übrigen Angaben an.',

  // --- Warenkorb ---
  'korb.inDenWarenkorb': 'In den Warenkorb',
  'korb.wirdHinzugefuegt': 'Wird hinzugefügt …',
  'korb.mehrNichtVorraetig': 'Mehr ist nicht vorrätig',
  'korb.liegtEins': '1 Stück liegt bereits im Warenkorb.',
  'korb.liegenViele': '{anzahl} Stück liegen bereits im Warenkorb.',
  'korb.ansehen': 'Warenkorb ansehen',
  'korb.titel': 'Warenkorb',
  'korb.oeffnenLeer': 'Warenkorb öffnen, er ist leer',
  'korb.oeffnenAnzahl': 'Warenkorb öffnen, {anzahl} Artikel',
  'korb.schliessenAria': 'Warenkorb schließen',
  'korb.schliessen': 'Schließen',
  'korb.laedt': 'Warenkorb wird geladen …',
  'korb.leer': 'Ihr Warenkorb ist leer.',
  'korb.stoebern': 'Stöbern Sie im',
  'korb.sortiment': 'Sortiment',
  'korb.zwischensumme': 'Zwischensumme',
  'korb.freigrenze': 'Noch {betrag} bis zum kostenlosen Versand.',
  'korb.versandhinweis': 'inkl. MwSt. Versandkosten werden an der Kasse berechnet.',
  'korb.unbestellbar':
    'Bitte entfernen Sie die nicht mehr verfügbaren Zeilen, dann geht es zur Kasse weiter.',
  // --- Rabattcode ---
  // --- Wertgutschein an der Kasse ---
  'gutschein.titel': 'Gutschein',
  'gutschein.haben': 'Ich habe einen Gutschein',
  'gutschein.label': 'Gutscheincode',
  'gutschein.platzhalter': 'Gutscheincode',
  'gutschein.hinzufuegen': 'Einlösen',
  'gutschein.prueft': 'Wird geprüft …',
  'gutschein.guthaben': 'Guthaben',
  'gutschein.schonDa': 'Dieser Gutschein ist bereits hinterlegt.',
  'gutschein.unbekannt': 'Diesen Gutschein gibt es nicht. Bitte prüfen Sie die Eingabe.',
  'gutschein.nichtEinloesbar': 'Dieser Gutschein ist nicht einlösbar.',
  'gutschein.fehler': 'Der Gutschein ließ sich gerade nicht prüfen. Bitte versuchen Sie es noch einmal.',
  'gutschein.restOffen': 'Nach dem Gutschein bleiben {betrag} zu zahlen.',
  'gutschein.decktAlles': 'Das Guthaben deckt die ganze Bestellung.',
  'rabatt.haben': 'Ich habe einen Rabattcode',
  'rabatt.label': 'Rabattcode',
  'rabatt.platzhalter': 'Rabattcode',
  'rabatt.einloesen': 'Einlösen',
  'rabatt.entfernen': 'Entfernen',
  'rabatt.gewaehrt': 'Rabatt',
  'korb.zurKasse': 'Zur Kasse',
  'korb.weiterEinkaufen': 'Weiter einkaufen',
  'korb.jeStueck': '{preis} je Stück',
  'korb.gesamtZeile': '{preis} gesamt',
  'korb.gekuerzt': 'Die Menge wurde auf {anzahl} verringert — mehr ist nicht vorrätig.',
  'korb.zeileWeg': 'Dieser Artikel ist nicht mehr verfügbar. Bitte entfernen Sie die Zeile.',
  'korb.entfernen': 'Entfernen',
  'korb.mengeVerringern': 'Menge von {name} verringern',
  'korb.mengeErhoehen': 'Menge von {name} erhöhen',
  'korb.menge': 'Menge von {name}',
  'korb.unbekannterArtikel': 'Unbekannter Artikel',

  // --- Kasse ---
  'kasse.seoTitel': 'Kasse — {laden}',
  'kasse.seoText': 'Bestellung abschließen.',
  'kasse.titel': 'Kasse',
  'kasse.korbLeer': 'Ihr Warenkorb ist leer. Legen Sie etwas hinein, dann geht es hier weiter —',
  'kasse.zumSortiment': 'zum Sortiment',
  'kasse.kontakt': 'Kontakt',
  'kasse.kontaktAngemeldet': 'Sie sind angemeldet. Prüfen Sie kurz, ob die Angaben stimmen.',
  'kasse.kontaktGast': 'Eine Bestellung ohne Konto. Wir brauchen nur, womit wir Sie erreichen.',
  'kasse.email': 'E-Mail-Adresse',
  'kasse.emailHinweis':
    'Hierhin geht die Bestätigung. Mit ihr und der Bestellnummer sehen Sie später den Stand nach.',
  'kasse.name': 'Vor- und Nachname',
  'kasse.telefon': 'Telefon',
  'kasse.telefonHinweis': 'Nur für Rückfragen zur Lieferung.',
  'kasse.zustellung': 'Versand oder Abholung',
  'kasse.versand': 'Versand',
  'kasse.versandText': 'Wir schicken die Bestellung an Ihre Adresse.',
  'kasse.abholung': 'Abholung',
  'kasse.abholungText':
    'Sie holen die Bestellung im Laden ab. Wir sagen Bescheid, sobald sie bereitliegt.',
  'kasse.lieferadresse': 'Lieferadresse',
  // Der Rückblick im letzten Schritt: Was eingegeben wurde, bevor es
  // verbindlich wird.
  'kasse.angaben': 'Ihre Angaben',
  'kasse.angabenAendern': '{was} ändern',
  'kasse.aendern': 'Ändern',
  'kasse.angabenLieferung': 'Lieferung',
  'kasse.angabenAbholung': 'Sie holen die Bestellung im Laden ab.',
  'kasse.empfaenger': 'Abweichender Empfänger',
  'kasse.empfaengerHinweis': 'Leer lassen, wenn das Paket an {name} geht.',
  'kasse.sieSelbst': 'Sie selbst',
  'kasse.firma': 'Firma',
  'kasse.strasse': 'Straße und Hausnummer',
  'kasse.zusatz': 'Adresszusatz',
  'kasse.zusatzHinweis': 'Wohnung, Hinterhaus, c/o — was die Zustellung sonst nicht findet.',
  'kasse.plz': 'Postleitzahl',
  'kasse.ort': 'Ort',
  'kasse.land': 'Land',
  'kasse.landDE': 'Deutschland',
  'kasse.landAT': 'Österreich',
  'kasse.landCH': 'Schweiz',
  'kasse.zahlungsart': 'Zahlungsart',
  'kasse.anmerkung': 'Anmerkung',
  'kasse.nachricht': 'Nachricht an den Laden',
  'kasse.nachrichtHinweis': 'Wunschtermin, Geschenkverpackung, Hinweise zur Abholung.',
  'kasse.uebersicht': 'Bestellübersicht',
  'kasse.uebersichtEinblenden': 'Bestellübersicht einblenden',
  'kasse.uebersichtAusblenden': 'Bestellübersicht ausblenden',
  'kasse.schritteAria': 'Schritte der Kasse',
  'kasse.schrittZaehler': 'Schritt {nummer} von {anzahl}',
  'kasse.schrittKontakt': 'Kontakt',
  'kasse.schrittVersand': 'Versand',
  'kasse.schrittZahlung': 'Zahlung',
  'kasse.schrittErledigt': 'erledigt',
  'kasse.schrittAktuell': 'aktueller Schritt',
  'kasse.schrittOffen': 'steht noch aus',
  'kasse.weiter': 'Weiter',
  'kasse.weiterZu': 'Weiter zu {schritt}',
  'kasse.zurueck': 'Zurück',
  'kasse.zurueckZu': 'Zurück zu {schritt}',
  'kasse.zeileWeg': 'Nicht mehr verfügbar. Bitte im Warenkorb entfernen.',
  'kasse.versandKostenlos': 'kostenlos',
  'kasse.versandEntfaellt': 'entfällt',
  'kasse.gesamt': 'Gesamt',
  'kasse.preishinweis': 'Alle Preise inklusive Mehrwertsteuer.',
  'kasse.preishinweisVersand': 'Die Versandkosten rechnet der Laden nach seinen Regeln.',
  'kasse.preishinweisAbholung': 'Bei Abholung fallen keine Versandkosten an.',
  'kasse.artikelWeg':
    'Ein Artikel im Warenkorb ist nicht mehr verfügbar. Bitte entfernen Sie die Zeile im Warenkorb.',
  'kasse.bitteErgaenzen': 'Bitte ergänzen Sie die oben rot gekennzeichneten Angaben.',
  'kasse.widerrufVor': 'Es gilt unsere',
  'kasse.widerrufNach':
    '. Sie öffnet sich in einem neuen Fenster, damit Ihre Angaben stehen bleiben.',
  'kasse.bestellen': 'Zahlungspflichtig bestellen',
  'kasse.bestelltGerade': 'Bestellung wird aufgegeben …',
  'kasse.schlusshinweis':
    'Mit dem Bestellen geben Sie eine verbindliche Bestellung ab. Die Bestätigung mit der Bestellnummer erhalten Sie per E-Mail. Bezahlt wird im nächsten Schritt.',
  'kasse.fehlerAllgemein':
    'Die Bestellung konnte nicht aufgegeben werden. Bitte versuchen Sie es in einem Moment noch einmal — abgebucht wurde nichts.',
  'kasse.fehlerEmailLeer':
    'Bitte geben Sie eine E-Mail-Adresse an — unter ihr finden Sie die Bestellung später wieder.',
  'kasse.fehlerEmailForm':
    'Diese Adresse sieht unvollständig aus. Eine E-Mail-Adresse enthält ein @ und danach einen Punkt.',
  'kasse.fehlerName': 'Bitte geben Sie Ihren Namen an, damit wir wissen, wer bestellt hat.',
  'kasse.fehlerStrasse': 'Bitte geben Sie Straße und Hausnummer an.',
  'kasse.fehlerPlzLeer': 'Bitte geben Sie die Postleitzahl an.',
  'kasse.fehlerPlzForm': 'Eine deutsche Postleitzahl besteht aus fünf Ziffern, zum Beispiel 86150.',
  'kasse.fehlerOrt': 'Bitte geben Sie den Ort an.',

  // --- Bezahlen ---
  'bezahlen.aria': 'Bezahlen',
  'bezahlen.titel': 'Bezahlen',
  'bezahlen.jetzt': 'Jetzt bezahlen',
  'bezahlen.keineArten':
    'Dieser Laden nimmt derzeit keine Zahlung über das Ladenfenster an. Er meldet sich zur Bezahlung bei Ihnen.',
  'bezahlen.artenLaden': 'Zahlungsarten werden geladen …',
  'bezahlen.abgelaufen':
    'Die Zahlungsanforderung ist abgelaufen. Ihre Bestellung ist weiterhin gültig — fordern Sie die Zahlung einfach noch einmal an.',
  'bezahlen.gescheitert': 'Die Zahlung ist nicht zustande gekommen.',
  'bezahlen.nochEinmal': ' Sie können es noch einmal versuchen.',
  'bezahlen.andereArt': ' Bitte wählen Sie eine andere Zahlungsart.',
  'bezahlen.nichtAngefordert': 'Die Zahlung ließ sich nicht anfordern.',
  'bezahlen.wirdVorbereitet': 'Zahlung wird vorbereitet …',
  'bezahlen.mitArt': 'Mit {art} bezahlen',
  'bezahlen.artWaehlen': 'Zahlungsart wählen',
  'bezahlen.zuZahlen': 'Zu zahlen sind',
  'bezahlen.anEmpfaenger': 'an {empfaenger}',
  'bezahlen.warten':
    'Wir warten auf Ihre Zahlung. Diese Seite wechselt von allein, sobald sie eingegangen ist — Sie müssen nichts weiter tun.',
  'bezahlen.seiteSchliessen':
    'Sie können die Seite auch schließen. Die Zahlung wird trotzdem verbucht, und die Bestätigung kommt per E-Mail.',
  'bezahlen.andereWaehlen': 'Andere Zahlungsart wählen',
  'bezahlen.codeAlt': 'QR-Code zum Bezahlen von {betrag}',
  'bezahlen.aufDiesemGeraet': 'Auf diesem Gerät bezahlen',
  'bezahlen.seiteNichtOffen':
    'Die Bezahlseite ließ sich nicht öffnen. Bitte wählen Sie eine andere Zahlungsart.',
  'bezahlen.weiterZu': 'Weiter zu {art}',
  'bezahlen.weiterZurSeite': 'Weiter zur Bezahlseite',
  'bezahlen.manuellStandard': 'Ihre Bestellung ist aufgegeben. Der Laden meldet sich zur Zahlung.',
  'bezahlen.manuellNachsatz':
    'Sobald die Zahlung bei uns eingegangen ist, ändert sich der Stand der Bestellung. Eine Bestätigung kommt per E-Mail.',

  // --- Bestellung: Stand ---
  'stand.pending': 'Eingegangen',
  'stand.confirmed': 'Bestätigt',
  'stand.awaiting_payment': 'Wartet auf Zahlung',
  'stand.draft': 'Entwurf',
  'stand.paid': 'Bezahlt',
  'stand.processing': 'Wird zusammengestellt',
  'stand.packed': 'Verpackt',
  'stand.ready': 'Liegt zur Abholung bereit',
  'stand.shipped': 'Versandt',
  'stand.delivered': 'Zugestellt',
  'stand.completed': 'Abgeschlossen',
  'stand.picked_up': 'Abgeholt',
  'stand.cancelled': 'Storniert',
  'stand.refunded': 'Erstattet',

  // --- Bestellung: Fortschritt ---
  'verfolgung.aria': 'Stand der Bestellung',
  'verfolgung.eingegangen': 'Eingegangen',
  'verfolgung.bezahlt': 'Bezahlt',
  'verfolgung.inBearbeitung': 'In Bearbeitung',
  'verfolgung.versandt': 'Versandt',
  'verfolgung.zugestellt': 'Zugestellt',
  'verfolgung.abholbereit': 'Abholbereit',
  'verfolgung.abgeholt': 'Abgeholt',
  'verfolgung.erledigt': 'erledigt',
  'verfolgung.offen': 'steht noch aus',
  'verfolgung.abgebrochen': 'Diese Bestellung wurde nicht zu Ende geführt.',
  'verfolgung.wartetAufZahlung': 'Wir warten auf Ihre Zahlung.',

  // --- Bestellung: Sendung ---
  'sendung.titel': 'Sendung',
  'sendung.titelMehrere': 'Sendung {nummer} von {anzahl}',
  'sendung.paketdienst': 'Paketdienst',
  'sendung.derPaketdienst': 'dem Paketdienst',
  'sendung.nummer': 'Sendungsnummer',
  'sendung.kopieren': 'Nummer kopieren',
  'sendung.kopiert': 'Kopiert',
  'sendung.verfolgen': 'Sendung verfolgen',
  'sendung.verfolgenNeuesFenster': 'Sendung verfolgen (öffnet ein neues Fenster)',
  'sendung.versandtAm': 'Versandt am {datum}',
  'sendung.zugestelltAm': 'Zugestellt am {datum}',

  // --- Bestellung: Ansicht ---
  'bestellung.aria': 'Bestellung {nummer}',
  'bestellung.positionen': 'Ihre Bestellung',
  'bestellung.stueck': '{anzahl} ×',
  'bestellung.jeStueck': 'je {preis}',
  'bestellung.zwischensumme': 'Zwischensumme',
  'bestellung.versand': 'Versand',
  'bestellung.versandEntfaellt': 'entfällt',
  'bestellung.versandKostenlos': 'kostenlos',
  'bestellung.gesamt': 'Gesamt',
  'bestellung.gutschein': 'Gutschein',
  'bestellung.nochZuZahlen': 'Noch zu zahlen',
  'bestellung.steuer': 'Im Gesamtbetrag enthalten: {betrag} Mehrwertsteuer.',
  'bestellung.aufgegebenAm': 'Aufgegeben am {datum}',
  'bestellung.bestelltAm': 'Bestellt am {datum}',
  'bestellung.nummerKopieren': 'Bestellnummer kopieren',
  'bestellung.nummerKopiert': 'Kopiert',
  'bestellung.nummerNichtKopiert':
    'Das Kopieren hat nicht geklappt. Sie können die Nummer daneben markieren.',
  'bestellung.fortschritt': 'Fortschritt',
  'bestellung.artikel': 'Bestellte Artikel',
  'bestellung.zahlung': 'Zahlung',
  'bestellung.steuerZeile': 'Enthaltene Steuer',
  'bestellung.lieferanschrift': 'Lieferanschrift',
  'bestellung.abholung': 'Abholung im Laden',
  'bestellung.abholungText':
    'Diese Bestellung wird nicht versandt. Sie holen sie im Laden ab — wir melden uns, sobald sie bereitliegt.',
  'bestellung.keineAnschrift': 'Zu dieser Bestellung ist keine Lieferanschrift hinterlegt.',
  'bestellung.rechnung': 'Rechnung herunterladen',
  'bestellung.rechnungNeuesFenster': 'Rechnung herunterladen (öffnet ein neues Fenster)',
  'bestellung.nochEinmal': 'Noch einmal bestellen',
  'bestellung.legtAn': 'Wird in den Warenkorb gelegt …',
  'bestellung.nachbestelltAlle': 'Alles liegt wieder im Warenkorb.',
  'bestellung.nachbestelltTeil':
    '{anzahl} von {gesamt} Positionen liegen wieder im Warenkorb. Die übrigen sind nicht mehr erhältlich.',
  'bestellung.nachbestelltKeine':
    'Keine Position dieser Bestellung ist noch erhältlich. Der Warenkorb bleibt, wie er war.',

  // --- Bestellung: Nachschau ---
  'nachschau.seoTitel': 'Bestellung {nummer} — {laden}',
  'nachschau.seoTitelLeer': 'Bestellung nachsehen — {laden}',
  'nachschau.seoText':
    'Bestellnummer und E-Mail-Adresse eingeben, um den Stand der Bestellung zu sehen.',
  'nachschau.krume': 'Bestellung',
  // Solange die Zahlung aussteht, ist die Bestellung nicht durch — dann darf
  // dort auch nicht gedankt werden.
  'nachschau.offenTitel': 'Fast geschafft — die Zahlung fehlt noch',
  'nachschau.offenText':
    'Ihre Bestellung liegt vor, und die Ware ist für Sie zurückgelegt. Verbindlich wird sie, sobald die Zahlung bei uns eingegangen ist. Bitte bezahlen Sie dazu unten.',
  'nachschau.dankeTitel': 'Vielen Dank für Ihre Bestellung',
  'nachschau.dankeText':
    'Ihre Bestellung ist bei {laden} eingegangen. Eine Bestätigung geht an die angegebene E-Mail-Adresse.',
  'nachschau.nummerLabel': 'Bestellnummer',
  // Kein Auftrag mehr an den Kunden, sondern eine Auskunft.
  //
  // „Notieren Sie sich die Nummer" war die Folge eines Fehlers: Die Bestellung
  // war nur mit Nummer und E-Mail erreichbar, und beides ging beim Neuladen
  // verloren. Jetzt traegt die Adresse den Schluessel, und die
  // Bestaetigungsmail traegt den Verweis — es gibt nichts mehr aufzuschreiben.
  'nachschau.notieren':
    'Diese Seite können Sie als Lesezeichen speichern. Den Verweis darauf finden Sie auch in der Bestätigungsmail — oder Sie sehen unter {verweis} mit Bestellnummer und E-Mail-Adresse nach.',
  'nachschau.titel': 'Bestellung nachsehen',
  'nachschau.ihreBestellung': 'Ihre Bestellung',
  'nachschau.zurueck': 'Andere Bestellung suchen',
  'nachschau.text':
    'Geben Sie die Bestellnummer aus der Bestätigung ein und die E-Mail-Adresse, mit der bestellt wurde. Beides zusammen ist der Schlüssel — ein Konto gibt es nicht.',
  'nachschau.nummerPlatzhalter': 'z. B. B-1042',
  'nachschau.emailLabel': 'E-Mail-Adresse',
  'nachschau.knopf': 'Bestellung anzeigen',
  'nachschau.sucht': 'Wird gesucht …',
  'nachschau.fehlerNummer': 'Bitte geben Sie die Bestellnummer aus der Bestätigung an.',
  'nachschau.fehlerEmail': 'Bitte geben Sie die E-Mail-Adresse an, mit der bestellt wurde.',
  // Der Verweis aus der Bestätigungsmail oder von der Bezahlseite passt
  // nicht mehr. Ein eigener Satz und nicht „nicht gefunden": Der Kunde hat
  // nichts falsch gemacht, und das Formular darunter hilft ihm weiter.
  'nachschau.linkAbgelaufen':
    'Dieser Verweis führt nicht mehr zu Ihrer Bestellung. Mit Bestellnummer und E-Mail-Adresse finden Sie sie unten.',
  'nachschau.nichtGefunden':
    'Zu dieser Nummer und E-Mail-Adresse finden wir nichts. Bitte prüfen Sie beides — die E-Mail muss die sein, mit der bestellt wurde.',
  'nachschau.fehler':
    'Die Bestellung lässt sich gerade nicht abrufen. Bitte versuchen Sie es in einem Moment noch einmal.',

  // --- Meine Bestellungen ---
  'meine.seoTitel': 'Meine Bestellungen — {laden}',
  'meine.seoText': 'Alle Bestellungen des angemeldeten Kontos.',
  'meine.titel': 'Meine Bestellungen',
  'meine.laedt': 'Bestellungen werden geladen …',
  'meine.nurAngemeldet': 'Dafür müssen Sie angemeldet sein. Ohne Konto sehen Sie eine Bestellung über Nummer und E-Mail-Adresse nach —',
  'meine.diesesKonto': 'diesem Konto',
  'meine.text':
    'Alle Bestellungen, die unter {konto} aufgegeben wurden. Was Sie vor der Anmeldung als Gast bestellt haben, finden Sie unter {verweis}.',
  'meine.leer': 'Hier ist noch nichts.',
  'meine.artikelEiner': '1 Artikel',
  'meine.artikelViele': '{anzahl} Artikel',
  'meine.ansehen': 'Bestellung ansehen',
  'meine.einblenden': 'Einzelheiten anzeigen',
  'meine.ausblenden': 'Einzelheiten ausblenden',

  // --- 404 ---
  'nichtGefunden.seoTitel': 'Seite nicht gefunden — {laden}',
  'nichtGefunden.seoText': 'Die aufgerufene Seite gibt es nicht.',
  'nichtGefunden.titel': 'Diese Seite gibt es nicht',
  'nichtGefunden.standard': 'Die aufgerufene Adresse führt ins Leere.',
  'nichtGefunden.text':
    'Möglicherweise hat sich ein Tippfehler eingeschlichen, oder das Produkt ist nicht mehr im Sortiment.',
  'nichtGefunden.zurStartseite': 'Zur Startseite',
  'nichtGefunden.zuSammlungen': 'Zu den Sammlungen',
  'nichtGefunden.alleProdukte': 'Alle Produkte ansehen',
  'seite.laedt': 'Seite wird geladen …',
  'seite.nichtGefunden': 'Diese Seite gibt es in diesem Laden nicht.',
} as const

/** Jeder Schlüssel, den es gibt. Die Liste entsteht aus dem Deutschen. */
export type Textschluessel = keyof typeof de

/**
 * Englisch.
 *
 * `Record<Textschluessel, string>` ist der ganze Zweck: Eine fehlende Zeile ist
 * ein Übersetzungsfehler und soll beim Bauen auffallen, nicht später im Laden
 * eines Kunden. Eine überzählige übrigens auch — sie wäre ein Schlüssel, den
 * das Deutsche nicht mehr kennt.
 */
const en: Record<Textschluessel, string> = {
  'app.oeffnet': 'Opening the shop …',
  'app.fehlerTitel': 'This shop is not reachable right now',
  'app.fehlerText':
    'We could not load this shop’s details. Please try again in a few minutes.',
  'layout.zumInhalt': 'Skip to content',
  'nav.aria': 'Main navigation',
  'nav.startseite': 'Home',
  'nav.sammlungen': 'Collections',
  'nav.bestellungVerfolgen': 'Track order',
  'nav.meineBestellungen': 'My orders',
  'nav.suchen': 'Search',
  'suche.feldLabel': 'Search this shop',
  'suche.platzhalter': 'Search …',
  'suche.knopf': 'Search',
  'suche.fensterHinweis': 'Search by product name, or leave empty to browse everything.',
  'fuss.rechtlichesAria': 'Legal and information',
  'fuss.mwst': 'All prices include VAT.',
  'sprache.label': 'Language',
  'sammlung.sortieren': 'Sort',
  'sammlung.sortEmpfohlen': 'Featured',
  'sammlung.sortPreisAuf': 'Price: low to high',
  'sammlung.sortPreisAb': 'Price: high to low',
  'sammlung.sortName': 'Name: A–Z',
  'home.sortimentAnsehen': 'Shop all',
  'home.sammlungenEntdecken': 'Explore collections',
  'home.sammlungenTitel': 'Shop by collection',
  'konto.menue': 'Account',
  'konto.menueAngemeldet': 'Account of {name}',
  'nav.menue': 'Menu',
  'nav.menueSchliessen': 'Close menu',
  'fuss.einkaufen': 'Shop',
  'fuss.service': 'Help',
  'fuss.rechtliches': 'Legal',
  'fuss.rechte': '© {jahr} {laden}',
  'kachel.ausverkauftMarke': 'Sold out',
  'kachel.inDenKorb': 'Add {name} to cart',
  'kachel.gelegt': '{name} is in your cart.',
  'kachel.nichtGelegt': '{name} is no longer in stock.',
  'produkt.menge': 'Quantity',
  'produkt.mengeWeniger': 'One less',
  'produkt.mengeMehr': 'One more',
  'produkt.versandTitel': 'Delivery',
  'produkt.versandFrei': 'Free delivery',
  'produkt.versandMitGrenze': '{preis}, free from {grenze}',
  'produkt.versandPauschal': '{preis} per order',
  'produkt.abholungTitel': 'Pick up in store',
  'produkt.abholungText': 'Free. We\'ll let you know as soon as it\'s ready.',
  'produkt.versandRueckgabe': 'Delivery and returns',
  'produkt.versandRueckgabeText': 'What delivery costs, how long it takes and how to return something is set out in our terms.',
  'meldung.aria': 'Notifications',
  'meldung.schliessen': 'Dismiss notification',

  'konto.angemeldet': 'Signed in',
  'konto.abmelden': 'Sign out',
  'konto.meldetAb': 'Signing out …',
  'konto.anmelden': 'Sign in',
  'anmeldung.abgebrochen': 'Signing in was cancelled. You can order without an account as well.',
  'anmeldung.abgelaufen': 'Signing in took too long. Please try again.',
  'anmeldung.fehlgeschlagen':
    'Signing in did not work. Please try again, or order without an account.',

  'zustand.laedt': 'Loading …',
  'zustand.nichtErreichbar': 'The shop is not reachable right now. Please try again in a moment.',
  'zustand.schiefgegangen': 'Something went wrong while loading. Please reload the page.',
  'zustand.meldungServer': 'Message from the server: ',

  'feld.freiwillig': '(optional)',

  'krumen.aria': 'You are here',
  'blaettern.aria': 'Result pages',
  'blaettern.zurueck': 'Back',
  'blaettern.weiter': 'Next',
  'blaettern.seiteVon': 'Page {seite} of {seiten}',

  'kachel.ab': 'from {preis}',
  'kachel.ausverkauft': 'currently sold out',
  'kachel.ohneBild': 'no image',
  'kachel.keinBildZu': 'No image for {name}',
  'galerie.keinBildZu': 'No image available for {name}',
  'galerie.bildAnzeigen': 'Show image {nummer} of {anzahl}',

  'home.seoTitel': '{laden} — online shop',
  'home.seoText': 'The online shop of {laden}. Browse collections and products.',
  'home.alleSammlungen': 'All collections',
  'home.sortiment': 'From the range',
  'home.allesAnsehen': 'See everything',
  'home.zusagenAria': 'What this shop promises',

  'sammlung.eine': 'Collection',
  'sammlung.seoTitelAlle': 'Collections — {laden}',
  'sammlung.seoTextAlle': 'All collections in the online shop of {laden}.',
  'sammlung.seoTitel': '{name} — {laden}',
  'sammlung.seoText': 'Products in the collection {name} at {laden}.',
  'sammlung.keine': 'This shop has not created any collections yet.',
  'sammlung.laedt': 'Loading collections …',
  'sammlung.leer': 'There is nothing in this collection at the moment.',
  'sammlung.nichtGefunden': 'This collection does not exist (any more).',
  'sammlung.produktEins': '1 product',
  'sammlung.produktViele': '{anzahl} products',

  'suche.seoTitel': 'Search for “{begriff}” — {laden}',
  'suche.seoTitelAlle': 'All products — {laden}',
  'suche.seoText': 'Search results for “{begriff}” in the online shop of {laden}.',
  'suche.seoTextAlle': 'All products in the online shop of {laden}.',
  'suche.krume': 'Search',
  'suche.alleProdukte': 'All products',
  'suche.ueberschrift': 'Search for “{begriff}”',
  'suche.trefferKeine': 'No matches',
  'suche.trefferEiner': '1 match',
  'suche.trefferViele': '{anzahl} matches',
  'suche.nichtsGefunden': 'We found nothing for “{begriff}”. A shorter word might help.',
  'produkt.keine': 'No products have been published yet.',

  'produkt.seoTitel': '{name} — {laden}',
  'produkt.seoTitelLeer': 'Product — {laden}',
  'produkt.nichtGefunden': 'This product does not exist (any more).',
  'produkt.preisAufAnfrage': 'Price on request',
  'produkt.abPreis': 'from {preis}',
  'produkt.steuerhinweis': 'incl. VAT, plus shipping',
  'produkt.beschreibung': 'Description',
  'produkt.keineZusammenstellung': 'This combination does not exist. Please choose another one.',
  'produkt.ausverkauft': 'Sold out',
  'produkt.andereAuswahl': ' — another choice may be in stock.',
  'produkt.vorraetig': 'In stock',
  'produkt.nurNoch': ' — only {anzahl} left',
  'variante.ausverkauft': 'sold out',
  'variante.nichtKombinierbar': 'not available together',
  'variante.nichtKombinierbarTitel':
    '{achse} {wert} does not exist with the current selection — one click adjusts the other choices.',

  'korb.inDenWarenkorb': 'Add to basket',
  'korb.wirdHinzugefuegt': 'Adding …',
  'korb.mehrNichtVorraetig': 'No more in stock',
  'korb.liegtEins': '1 item is already in your basket.',
  'korb.liegenViele': '{anzahl} items are already in your basket.',
  'korb.ansehen': 'View basket',
  'korb.titel': 'Basket',
  'korb.oeffnenLeer': 'Open basket, it is empty',
  'korb.oeffnenAnzahl': 'Open basket, {anzahl} items',
  'korb.schliessenAria': 'Close basket',
  'korb.schliessen': 'Close',
  'korb.laedt': 'Loading basket …',
  'korb.leer': 'Your basket is empty.',
  'korb.stoebern': 'Have a look at the',
  'korb.sortiment': 'range',
  'korb.zwischensumme': 'Subtotal',
  'korb.freigrenze': '{betrag} to go until shipping is free.',
  'korb.versandhinweis': 'incl. VAT. Shipping is calculated at the checkout.',
  'korb.unbestellbar':
    'Please remove the lines that are no longer available, then you can continue to the checkout.',
  'gutschein.titel': 'Gift card',
  'gutschein.haben': 'I have a gift card',
  'gutschein.label': 'Gift card code',
  'gutschein.platzhalter': 'Gift card code',
  'gutschein.hinzufuegen': 'Apply',
  'gutschein.prueft': 'Checking …',
  'gutschein.guthaben': 'Balance',
  'gutschein.schonDa': 'This gift card is already applied.',
  'gutschein.unbekannt': 'No such gift card. Please check what you entered.',
  'gutschein.nichtEinloesbar': 'This gift card cannot be used.',
  'gutschein.fehler': 'The gift card could not be checked just now. Please try again.',
  'gutschein.restOffen': '{betrag} left to pay after the gift card.',
  'gutschein.decktAlles': 'The balance covers the whole order.',
  'rabatt.haben': 'I have a discount code',
  'rabatt.label': 'Discount code',
  'rabatt.platzhalter': 'Discount code',
  'rabatt.einloesen': 'Apply',
  'rabatt.entfernen': 'Remove',
  'rabatt.gewaehrt': 'Discount',
  'korb.zurKasse': 'Go to checkout',
  'korb.weiterEinkaufen': 'Keep shopping',
  'korb.jeStueck': '{preis} each',
  'korb.gesamtZeile': '{preis} in total',
  'korb.gekuerzt': 'The quantity was reduced to {anzahl} — there are no more in stock.',
  'korb.zeileWeg': 'This item is no longer available. Please remove the line.',
  'korb.entfernen': 'Remove',
  'korb.mengeVerringern': 'Decrease quantity of {name}',
  'korb.mengeErhoehen': 'Increase quantity of {name}',
  'korb.menge': 'Quantity of {name}',
  'korb.unbekannterArtikel': 'Unknown item',

  'kasse.seoTitel': 'Checkout — {laden}',
  'kasse.seoText': 'Complete your order.',
  'kasse.titel': 'Checkout',
  'kasse.korbLeer': 'Your basket is empty. Put something in and you can continue here —',
  'kasse.zumSortiment': 'browse the range',
  'kasse.kontakt': 'Contact',
  'kasse.kontaktAngemeldet': 'You are signed in. Please check that the details are correct.',
  'kasse.kontaktGast': 'An order without an account. We only need a way to reach you.',
  'kasse.email': 'Email address',
  'kasse.emailHinweis':
    'The confirmation goes here. With it and the order number you can check the status later.',
  'kasse.name': 'First and last name',
  'kasse.telefon': 'Phone',
  'kasse.telefonHinweis': 'Only for questions about the delivery.',
  'kasse.zustellung': 'Shipping or pickup',
  'kasse.versand': 'Shipping',
  'kasse.versandText': 'We send the order to your address.',
  'kasse.abholung': 'Pickup',
  'kasse.angaben': 'Your details',
  'kasse.angabenAendern': 'Change {was}',
  'kasse.aendern': 'Change',
  'kasse.angabenLieferung': 'Delivery',
  'kasse.angabenAbholung': 'You will pick the order up in the shop.',
  'kasse.abholungText': 'You collect the order in the shop. We let you know as soon as it is ready.',
  'kasse.lieferadresse': 'Delivery address',
  'kasse.empfaenger': 'Different recipient',
  'kasse.empfaengerHinweis': 'Leave empty if the parcel goes to {name}.',
  'kasse.sieSelbst': 'you',
  'kasse.firma': 'Company',
  'kasse.strasse': 'Street and house number',
  'kasse.zusatz': 'Address addition',
  'kasse.zusatzHinweis': 'Flat, rear building, c/o — whatever the delivery would not find otherwise.',
  'kasse.plz': 'Postcode',
  'kasse.ort': 'Town',
  'kasse.land': 'Country',
  'kasse.landDE': 'Germany',
  'kasse.landAT': 'Austria',
  'kasse.landCH': 'Switzerland',
  'kasse.zahlungsart': 'Payment method',
  'kasse.anmerkung': 'Note',
  'kasse.nachricht': 'Message to the shop',
  'kasse.nachrichtHinweis': 'Preferred date, gift wrapping, notes about the pickup.',
  'kasse.uebersicht': 'Order summary',
  'kasse.uebersichtEinblenden': 'Show order summary',
  'kasse.uebersichtAusblenden': 'Hide order summary',
  'kasse.schritteAria': 'Checkout steps',
  'kasse.schrittZaehler': 'Step {nummer} of {anzahl}',
  'kasse.schrittKontakt': 'Contact',
  'kasse.schrittVersand': 'Shipping',
  'kasse.schrittZahlung': 'Payment',
  'kasse.schrittErledigt': 'done',
  'kasse.schrittAktuell': 'current step',
  'kasse.schrittOffen': 'still to come',
  'kasse.weiter': 'Continue',
  'kasse.weiterZu': 'Continue to {schritt}',
  'kasse.zurueck': 'Back',
  'kasse.zurueckZu': 'Back to {schritt}',
  'kasse.zeileWeg': 'No longer available. Please remove it from the basket.',
  'kasse.versandKostenlos': 'free',
  'kasse.versandEntfaellt': 'not applicable',
  'kasse.gesamt': 'Total',
  'kasse.preishinweis': 'All prices include VAT.',
  'kasse.preishinweisVersand': 'Shipping costs are calculated by the shop’s own rules.',
  'kasse.preishinweisAbholung': 'There are no shipping costs for a pickup.',
  'kasse.artikelWeg':
    'An item in your basket is no longer available. Please remove that line in the basket.',
  'kasse.bitteErgaenzen': 'Please complete the details marked in red above.',
  'kasse.widerrufVor': 'Our',
  'kasse.widerrufNach': ' applies. It opens in a new window so that your entries stay put.',
  'kasse.bestellen': 'Place binding order',
  'kasse.bestelltGerade': 'Placing the order …',
  'kasse.schlusshinweis':
    'By ordering you place a binding order. You will receive the confirmation with the order number by email. Payment follows in the next step.',
  'kasse.fehlerAllgemein':
    'The order could not be placed. Please try again in a moment — nothing has been charged.',
  'kasse.fehlerEmailLeer':
    'Please enter an email address — you will find the order again with it later.',
  'kasse.fehlerEmailForm':
    'This address looks incomplete. An email address contains an @ and a dot after it.',
  'kasse.fehlerName': 'Please enter your name so we know who ordered.',
  'kasse.fehlerStrasse': 'Please enter street and house number.',
  'kasse.fehlerPlzLeer': 'Please enter the postcode.',
  'kasse.fehlerPlzForm': 'A German postcode has five digits, for example 86150.',
  'kasse.fehlerOrt': 'Please enter the town.',

  'bezahlen.aria': 'Payment',
  'bezahlen.titel': 'Payment',
  'bezahlen.jetzt': 'Pay now',
  'bezahlen.keineArten':
    'This shop currently does not accept payments through the shop window. It will contact you about the payment.',
  'bezahlen.artenLaden': 'Loading payment methods …',
  'bezahlen.abgelaufen':
    'The payment request has expired. Your order is still valid — simply request the payment again.',
  'bezahlen.gescheitert': 'The payment did not go through.',
  'bezahlen.nochEinmal': ' You can try again.',
  'bezahlen.andereArt': ' Please choose a different payment method.',
  'bezahlen.nichtAngefordert': 'The payment could not be requested.',
  'bezahlen.wirdVorbereitet': 'Preparing the payment …',
  'bezahlen.mitArt': 'Pay with {art}',
  'bezahlen.artWaehlen': 'Choose a payment method',
  'bezahlen.zuZahlen': 'Amount due:',
  'bezahlen.anEmpfaenger': 'to {empfaenger}',
  'bezahlen.warten':
    'We are waiting for your payment. This page changes by itself as soon as it arrives — you do not need to do anything else.',
  'bezahlen.seiteSchliessen':
    'You can also close this page. The payment will still be recorded, and the confirmation arrives by email.',
  'bezahlen.andereWaehlen': 'Choose a different payment method',
  'bezahlen.codeAlt': 'QR code to pay {betrag}',
  'bezahlen.aufDiesemGeraet': 'Pay on this device',
  'bezahlen.seiteNichtOffen':
    'The payment page could not be opened. Please choose a different payment method.',
  'bezahlen.weiterZu': 'Continue to {art}',
  'bezahlen.weiterZurSeite': 'Continue to the payment page',
  'bezahlen.manuellStandard': 'Your order has been placed. The shop will get in touch about payment.',
  'bezahlen.manuellNachsatz':
    'As soon as the payment reaches us, the status of the order changes. A confirmation arrives by email.',

  'stand.pending': 'Received',
  'stand.confirmed': 'Confirmed',
  'stand.awaiting_payment': 'Awaiting payment',
  'stand.draft': 'Draft',
  'stand.paid': 'Paid',
  'stand.processing': 'Being prepared',
  'stand.packed': 'Packed',
  'stand.ready': 'Ready for pickup',
  'stand.shipped': 'Dispatched',
  'stand.delivered': 'Delivered',
  'stand.completed': 'Completed',
  'stand.picked_up': 'Picked up',
  'stand.cancelled': 'Cancelled',
  'stand.refunded': 'Refunded',

  'verfolgung.aria': 'Order status',
  'verfolgung.eingegangen': 'Received',
  'verfolgung.bezahlt': 'Paid',
  'verfolgung.inBearbeitung': 'Being prepared',
  'verfolgung.versandt': 'Dispatched',
  'verfolgung.zugestellt': 'Delivered',
  'verfolgung.abholbereit': 'Ready for pickup',
  'verfolgung.abgeholt': 'Picked up',
  'verfolgung.erledigt': 'done',
  'verfolgung.offen': 'still to come',
  'verfolgung.abgebrochen': 'This order was not completed.',
  'verfolgung.wartetAufZahlung': 'We are waiting for your payment.',

  'sendung.titel': 'Shipment',
  'sendung.titelMehrere': 'Shipment {nummer} of {anzahl}',
  'sendung.paketdienst': 'Carrier',
  'sendung.derPaketdienst': 'the carrier',
  'sendung.nummer': 'Tracking number',
  'sendung.kopieren': 'Copy number',
  'sendung.kopiert': 'Copied',
  'sendung.verfolgen': 'Track shipment',
  'sendung.verfolgenNeuesFenster': 'Track shipment (opens a new window)',
  'sendung.versandtAm': 'Dispatched on {datum}',
  'sendung.zugestelltAm': 'Delivered on {datum}',

  'bestellung.aria': 'Order {nummer}',
  'bestellung.positionen': 'Your order',
  'bestellung.stueck': '{anzahl} ×',
  'bestellung.jeStueck': '{preis} each',
  'bestellung.zwischensumme': 'Subtotal',
  'bestellung.versand': 'Shipping',
  'bestellung.versandEntfaellt': 'not applicable',
  'bestellung.versandKostenlos': 'free',
  'bestellung.gesamt': 'Total',
  'bestellung.gutschein': 'Gift card',
  'bestellung.nochZuZahlen': 'Left to pay',
  'bestellung.steuer': 'Included in the total: {betrag} VAT.',
  'bestellung.aufgegebenAm': 'Placed on {datum}',
  'bestellung.bestelltAm': 'Ordered on {datum}',
  'bestellung.nummerKopieren': 'Copy order number',
  'bestellung.nummerKopiert': 'Copied',
  'bestellung.nummerNichtKopiert':
    'Copying did not work. You can select the number next to the button.',
  'bestellung.fortschritt': 'Progress',
  'bestellung.artikel': 'Items ordered',
  'bestellung.zahlung': 'Payment',
  'bestellung.steuerZeile': 'VAT included',
  'bestellung.lieferanschrift': 'Delivery address',
  'bestellung.abholung': 'Pickup in the shop',
  'bestellung.abholungText':
    'This order will not be shipped. You collect it in the shop — we let you know as soon as it is ready.',
  'bestellung.keineAnschrift': 'There is no delivery address on this order.',
  'bestellung.rechnung': 'Download invoice',
  'bestellung.rechnungNeuesFenster': 'Download invoice (opens a new window)',
  'bestellung.nochEinmal': 'Order again',
  'bestellung.legtAn': 'Adding to the basket …',
  'bestellung.nachbestelltAlle': 'Everything is back in your basket.',
  'bestellung.nachbestelltTeil':
    '{anzahl} of {gesamt} items are back in your basket. The rest is no longer available.',
  'bestellung.nachbestelltKeine':
    'No item of this order is available any more. Your basket is unchanged.',

  'nachschau.seoTitel': 'Order {nummer} — {laden}',
  'nachschau.seoTitelLeer': 'Track your order — {laden}',
  'nachschau.seoText': 'Enter the order number and email address to see the status of your order.',
  'nachschau.krume': 'Order',
  'nachschau.offenTitel': 'Almost there — your payment is still open',
  'nachschau.offenText':
    'We have your order and set the goods aside. It becomes binding once the payment has reached us. Please pay below.',
  'nachschau.dankeTitel': 'Thank you for your order',
  'nachschau.dankeText':
    'Your order has reached {laden}. A confirmation is on its way to the email address you gave.',
  'nachschau.nummerLabel': 'Order number',
  'nachschau.notieren':
    'You can bookmark this page. The link is also in your confirmation email — or look the order up under {verweis} with your order number and email address.',
  'nachschau.titel': 'Track your order',
  'nachschau.ihreBestellung': 'Your order',
  'nachschau.zurueck': 'Look up another order',
  'nachschau.text':
    'Enter the order number from your confirmation and the email address the order was placed with. Together they are the key — there is no account.',
  'nachschau.nummerPlatzhalter': 'e.g. B-1042',
  'nachschau.emailLabel': 'Email address',
  'nachschau.knopf': 'Show order',
  'nachschau.sucht': 'Searching …',
  'nachschau.fehlerNummer': 'Please enter the order number from your confirmation.',
  'nachschau.fehlerEmail': 'Please enter the email address the order was placed with.',
  'nachschau.linkAbgelaufen':
    'This link no longer leads to your order. You can find it below using your order number and email address.',
  'nachschau.nichtGefunden':
    'We cannot find anything for this number and email address. Please check both — the email must be the one the order was placed with.',
  'nachschau.fehler': 'The order cannot be retrieved right now. Please try again in a moment.',

  'meine.seoTitel': 'My orders — {laden}',
  'meine.seoText': 'All orders of the signed-in account.',
  'meine.titel': 'My orders',
  'meine.laedt': 'Loading orders …',
  'meine.nurAngemeldet':
    'You need to be signed in for this. Without an account you look up an order with its number and email address —',
  'meine.diesesKonto': 'this account',
  'meine.text':
    'All orders placed under {konto}. Anything you ordered as a guest before signing in can be found under {verweis}.',
  'meine.leer': 'There is nothing here yet.',
  'meine.artikelEiner': '1 item',
  'meine.artikelViele': '{anzahl} items',
  'meine.ansehen': 'View order',
  'meine.einblenden': 'Show details',
  'meine.ausblenden': 'Hide details',

  'nichtGefunden.seoTitel': 'Page not found — {laden}',
  'nichtGefunden.seoText': 'The page you asked for does not exist.',
  'nichtGefunden.titel': 'This page does not exist',
  'nichtGefunden.standard': 'The address you called leads nowhere.',
  'nichtGefunden.text':
    'Perhaps there is a typo, or the product is no longer part of the range.',
  'nichtGefunden.zurStartseite': 'To the home page',
  'nichtGefunden.zuSammlungen': 'To the collections',
  'nichtGefunden.alleProdukte': 'See all products',
  'seite.laedt': 'Loading page …',
  'seite.nichtGefunden': 'This page does not exist in this shop.',
}

// ---------- Die Sprachen, die es gibt ----------

export type Sprache = 'de' | 'en'

/**
 * Was eine Sprache ausmacht.
 *
 * `locale` ist getrennt vom Kürzel, weil Intl mehr braucht als „de": Es
 * entscheidet über Komma und Punkt im Preis und über die Reihenfolge im Datum.
 * `name` steht in der eigenen Sprache — wer im deutschen Laden auf Englisch
 * umschalten will, sucht „English" und nicht „Englisch".
 */
interface Spracheintrag {
  code: Sprache
  name: string
  locale: string
  woerter: Record<Textschluessel, string>
}

export const SPRACHEN: Spracheintrag[] = [
  { code: 'de', name: 'Deutsch', locale: 'de-DE', woerter: de },
  // en-GB und nicht en-US: Der Laden steht in Europa, und ein englischer Text
  // mit amerikanischem Datum (9/12/2026) meint an einem deutschen Paket den
  // falschen Tag.
  { code: 'en', name: 'English', locale: 'en-GB', woerter: en },
]

const VORGABE: Sprache = 'de'
const SPEICHER = 'ahri-shop-sprache'

function eintrag(sprache: Sprache): Spracheintrag {
  return SPRACHEN.find((s) => s.code === sprache) ?? SPRACHEN[0]!
}

/**
 * Die Sprache, in der die Seite das erste Mal dasteht.
 *
 * Erst die Wahl des Besuchers — sie hat er selbst getroffen und gilt, bis er
 * sie ändert. Dann, was sein Browser sagt. Und wenn der etwas sagt, das dieser
 * Laden nicht spricht, bleibt es beim Deutschen: Der Laden steht hier, seine
 * Pflichtseiten sind deutsch, und eine unvollständige Fremdsprache wäre die
 * schlechtere Auskunft.
 */
function ersteSprache(): Sprache {
  try {
    const gemerkt = window.localStorage.getItem(SPEICHER)
    if (gemerkt && SPRACHEN.some((s) => s.code === gemerkt)) return gemerkt as Sprache
  } catch {
    /* Kein Speicher (privates Fenster, gesperrte Seitendaten) — dann eben nicht. */
  }

  // navigator.languages ist die Rangfolge des Besuchers, navigator.language nur
  // sein erster Wunsch. Wer Französisch bevorzugt, aber auch Englisch kann,
  // soll Englisch bekommen und nicht Deutsch.
  const wuensche = [...(navigator.languages ?? []), navigator.language].filter(Boolean)
  for (const wunsch of wuensche) {
    const kurz = wunsch.toLowerCase().split('-')[0]
    const treffer = SPRACHEN.find((s) => s.code === kurz)
    if (treffer) return treffer.code
  }
  return VORGABE
}

// ---------- Der Kontext ----------

export type Werte = Record<string, string | number>

interface Sprachlage {
  sprache: Sprache
  locale: string
  setzeSprache: (neu: Sprache) => void
  /** Ein Satz, samt eingesetzter Platzhalter: t('korb.freigrenze', { betrag }) */
  t: (schluessel: Textschluessel, werte?: Werte) => string
  /**
   * Einzahl oder Mehrzahl.
   *
   * Zwei Schlüssel und kein Regelwerk: Deutsch und Englisch unterscheiden
   * genau diese beiden Formen, und an den drei Stellen, an denen das
   * Ladenfenster zählt, ist das alles. Eine Sprache mit Dual oder Paukal
   * bekäme hier eine eigene Regel — aber erst, wenn sie dazukommt.
   */
  tz: (anzahl: number, einzahl: Textschluessel, mehrzahl: Textschluessel, werte?: Werte) => string
}

const SprachContext = createContext<Sprachlage | null>(null)

function setzeEin(vorlage: string, werte?: Werte): string {
  if (!werte) return vorlage
  return vorlage.replace(/\{(\w+)\}/g, (ganz, name: string) => {
    const wert = werte[name]
    // Ein unbekannter Platzhalter bleibt stehen, statt zu verschwinden: So
    // fällt beim Ansehen auf, dass ein Wert fehlt — ein leeres Loch im Satz
    // sieht aus wie ein Tippfehler des Ladens.
    return wert === undefined ? ganz : String(wert)
  })
}

export function SprachProvider({ children }: { children: ReactNode }) {
  const [sprache, setSprache] = useState<Sprache>(ersteSprache)

  // Das lang-Attribut zieht mit. Es ist keine Formsache: Danach entscheidet
  // die Sprachausgabe, mit welcher Aussprache sie liest, und der Browser,
  // welche Trennregeln und welches Anführungszeichen er nimmt.
  useEffect(() => {
    document.documentElement.lang = sprache
  }, [sprache])

  const setzeSprache = useCallback((neu: Sprache) => {
    setSprache(neu)
    try {
      window.localStorage.setItem(SPEICHER, neu)
    } catch {
      /* Ohne Speicher gilt die Wahl nur für diesen Besuch. */
    }
  }, [])

  const wert = useMemo<Sprachlage>(() => {
    const gewaehlt = eintrag(sprache)
    const t = (schluessel: Textschluessel, werte?: Werte) =>
      setzeEin(gewaehlt.woerter[schluessel], werte)

    return {
      sprache,
      locale: gewaehlt.locale,
      setzeSprache,
      t,
      tz: (anzahl, einzahl, mehrzahl, werte) =>
        t(anzahl === 1 ? einzahl : mehrzahl, { anzahl, ...werte }),
    }
  }, [sprache, setzeSprache])

  return <SprachContext.Provider value={wert}>{children}</SprachContext.Provider>
}

export function useSprache(): Sprachlage {
  const lage = useContext(SprachContext)
  if (!lage) throw new Error('useSprache außerhalb des SprachProvider verwendet')
  return lage
}

/**
 * Setzt ein Element mitten in einen übersetzten Satz.
 *
 * Ein Verweis steht in jeder Sprache an einer anderen Stelle im Satz; ihn
 * hinten anzuhängen ergäbe im Englischen einen anderen Satz als im Deutschen.
 * Deshalb steht im Wörterbuch ein Platzhalter, und der Satz wird hier an ihm
 * aufgeteilt.
 *
 * Ausdrücklich kein HTML in der Übersetzung: Das käme nur über
 * dangerouslySetInnerHTML in die Seite, und dann wäre eine Zeichenkette aus
 * einem Wörterbuch ein Einfallstor — dieselbe Überlegung wie in markdown.tsx.
 */
export function mitElement(satz: string, marke: string, element: ReactNode): ReactNode {
  const [vorher = '', nachher = ''] = satz.split(marke)
  return (
    <>
      {vorher}
      {element}
      {nachher}
    </>
  )
}

/** Nur die Übersetzung — der häufigste Fall, und dann steht kein Beiwerk herum. */
export function useT(): (schluessel: Textschluessel, werte?: Werte) => string {
  return useSprache().t
}

/**
 * Ein Datum, wie man es in der gewählten Sprache schreibt.
 *
 * Über Intl und nicht von Hand: Dass der 12.09.2026 auf Englisch 12/09/2026
 * heißt und nicht 09/12/2026, ist genau die Art Regel, die eine eigene
 * Formatierung beim dritten Land verliert. Unlesbares bleibt weg — ein
 * „Invalid Date" mitten in der Sendungsverfolgung ist schlimmer als eine
 * fehlende Zeile.
 */
export function useDatumFormat(): (roh: string | null | undefined, mitUhrzeit?: boolean) => string {
  const { locale } = useSprache()
  return useCallback(
    (roh, mitUhrzeit = false) => {
      if (!roh) return ''
      const wert = new Date(roh)
      if (Number.isNaN(wert.getTime())) return ''
      return wert.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(mitUhrzeit ? { hour: '2-digit', minute: '2-digit' } : {}),
      })
    },
    [locale],
  )
}
